import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import { GET_ACCOUNT_INFORMATION_LOADER } from "../../../store/myAccount/types";
import { Notification } from "../../common/Notification";
import * as httpClient from "../../../util/httpClient";
import i18nService from "../../../util/Internationalization/i18n/i18n";
import { DialogComponent } from "../../common/DialogComponent";
import DashboardSecurityQuestionDialog from "../../Dialogs/DashboardSecurityQuestionDialog";
import { getFormattedDate } from "../../../util/dateFormatting";
import RenderIcons from "../../../Assets/css/_icons/partner_icon/RenderIcons";
import { useTranslation } from "react-i18next";

const ManageAccountPassword = ({ showDashboardSecurityQuestionDialog, setShowDashboardSecurityQuestionDialog }: any) => {
    const dispatch = useDispatch();
    const { t: tLang } = useTranslation();
    const { register, handleSubmit, errors, watch, setError, getValues } = useForm({
        mode: "onTouched",
        criteriaMode: "all",
    });
    const [showEdit, setShowEdit] = useState(false);
    const [state, setState] = useState({
        firstInteractionDone: false,
    });
    const [isLoading, setIsLoading] = useState(false);
    const unifiedAccInfo = useSelector((state: ApplicationState) =>
        state && state.myAccountServiceState ? state.myAccountServiceState._unifiedAccountInfo : null
    );

    const accountInfo = useSelector((state: ApplicationState) => {
        if (state.myAccountServiceState && state.myAccountServiceState._unifiedAccountInfo && state.myAccountServiceState._unifiedAccountInfo._passwordSecurity) {
            return {
                lastPasswordChangeDate:
                    state.myAccountServiceState._unifiedAccountInfo._passwordSecurity &&
                    state.myAccountServiceState._unifiedAccountInfo._passwordSecurity.lastPasswordChangeDate,
                isSSOUser: state.myAccountServiceState._unifiedAccountInfo.isSSOUser ? state.myAccountServiceState._unifiedAccountInfo.isSSOUser : false,
            };
        } else {
            return {
                lastPasswordChangeDate: "",
            };
        }
    });

    const partnerPhone = useSelector(
        (state: ApplicationState) =>
            (state.configurationState &&
                state.configurationState.configuration &&
                state.configurationState.configuration.partnerConfig &&
                state.configurationState.configuration.partnerConfig.phoneNumber) ||
            ""
    );

    const getQuestionInfo = useSelector((state: ApplicationState) => {
        if (state.myAccountServiceState && state.myAccountServiceState.securityQueData) {
            return {
                securityQueData: state.myAccountServiceState.securityQueData,
            };
        } else {
            return {
                securityQueData: [],
            };
        }
    });

    let userData = useSelector((state: ApplicationState) => {
        let data: any = {
            email: "",
            isIDFUser: false,
            isSuccess: true,
            securityQuestionsCount: 3,
            portalName: "",
            isLanguageSupport: "true"
        };
        if (state.configurationState && state.configurationState.configuration && state.configurationState.configuration.partnerConfig != null) {
            data.portalName = state.configurationState.configuration.partnerConfig.partnerProvider;
        }

        if (unifiedAccInfo && unifiedAccInfo.email) {
            data.email = unifiedAccInfo.email;
        }
        if (state.csidUserStatusState && state.csidUserStatusState.csidUserStatus) {
            data.isIDFUser = state.csidUserStatusState.csidUserStatus.isIDFUser;
        }
        return data;
    });

    const showEditForm = () => {
        setShowEdit(true);
        setState({ ...state, firstInteractionDone: false });
    };

    const hideEditForm = () => {
        setShowEdit(false);
        setState({ ...state, firstInteractionDone: false });
    };

    const validation = {
        minLengthCharacter: (val: any) => /^.{12,}$/.test(val),
        containsCapitalLetter: (val: any) => /.*[A-Z].*/.test(val),
        containsSpecialLetter: (val: any) => /[*@!#%&()^~{}$\-_+=\[\]\|;:"'<,>\.\?]+/.test(val),
        containsSmallLetter: (val: any) => /.*[a-z].*/.test(val),
        containsNumber: (val: any) => /\d/.test(val),
    };

    const validationMessage: Record<string, string> = {
        minLengthCharacter: tLang("manageAccount.myAccount.manageAccountPassword.validation.minLengthCharacter"),
        containsCapitalLetter: tLang("manageAccount.myAccount.manageAccountPassword.validation.containsCapitalLetter"),
        containsSmallLetter: tLang("manageAccount.myAccount.manageAccountPassword.validation.containsSmallLetter"),
        containsNumber: tLang("manageAccount.myAccount.manageAccountPassword.validation.containsNumber"),
        containsSpecialLetter: tLang("manageAccount.myAccount.manageAccountPassword.validation.containsSpecialLetter"),
    };

    const getAccountInformation = () => {
        httpClient
            .sendRequest("GET", "api/accountInfo/getAccountInformation")
            .then((data) => {
                dispatch({ type: GET_ACCOUNT_INFORMATION_LOADER, isLoading: false });
                setShowEdit(false);
                if (data && data.hasError === false) {
                    if (data && data._unifiedAccountInfo && data._unifiedAccountInfo.isSSOUser === false) {
                        dispatch({ type: GET_ACCOUNT_INFORMATION_LOADER, isLoading: true });
                        httpClient
                            .sendRequest("POST", "api/security-question/get-security-questions", userData)
                            .then((res) => {
                                if (res && res.hasError === false) {
                                    dispatch({ type: "GET_SECURITY_QUESTIONS", securityQueData: res });
                                    if (res && res.selectedSecurityQuestion === null) {
                                        setShowDashboardSecurityQuestionDialog(true);
                                    }
                                }
                            })
                            .catch(function (error) {});
                    }
                    dispatch({
                        type: "GET_ACCOUNT_INFORMATION",
                        _unifiedAccountInfo: data._unifiedAccountInfo,
                    });
                }
            })
            .catch(function (error) {
                setShowEdit(false);
                dispatch({ type: GET_ACCOUNT_INFORMATION_LOADER, isLoading: false });
            });
    };

    const onSubmit = (data: any) => {
        if (getValues("confirmPassword") !== watch("newPassword")) {
            setError("confirmPassword", {
                type: "validate",
            });
        } else {
            setIsLoading(true);
            const url = accountInfo && accountInfo.isSSOUser ? "web/password/SSOChangePassword" : "api/accountInfo/ChangePassword";
            httpClient
                .sendRequest("POST", url, data)
                .then((res) => {
                    if (!res.isSuccess) {
                        setError("currentPassword", {
                            type: "isPasswordWrong",
                        });
                    } else {
                        getAccountInformation();
                        Notification({
                            type: "success",
                            title: tLang("manageAccount.myAccount.manageAccountPassword.notification.successTitle"),
                            message: tLang("manageAccount.myAccount.manageAccountPassword.notification.successMsg"),
                        });
                    }
                    setIsLoading(false);
                })
                .catch((err) => {
                    setIsLoading(false);
                    Notification({
                        type: "error",
                        title: "Error",
                        message: err,
                        duration: 0,
                    });
                });
        }
    };

    const closeSecurityQuestionDialog = () => {
        setShowDashboardSecurityQuestionDialog(false);
    };

    return (
        <React.Fragment>
            <div className={isLoading ? "box offset-padding loading" : "box offset-padding"}>
                <div className="box-header">
                    <div className="box-title-container">
                        <div className="box-title-icon" aria-hidden="true">
                            <RenderIcons iconName="key" />
                        </div>
                        <div className="box-title-text has-actions">
                            <h2 className="box-title">{tLang("manageAccount.myAccount.manageAccountPassword.title")}</h2>
                        </div>
                    </div>
                    <div className="box-description">
                        <p>{tLang("manageAccount.myAccount.manageAccountPassword.description")}</p>
                    </div>
                    {!showEdit && (
                        <div className="box-actions">
                            <button type="button" className="btn-default btn-grey-dot" id="password-and-security" onClick={showEditForm}>
                                <span className="btn-text">
                                    {tLang("manageAccount.myAccount.manageAccountPassword.editButtonText")}{" "}
                                    <span className="sr-only">{tLang("manageAccount.myAccount.manageAccountPassword.title")}</span>
                                </span>
                                <span className="btn-icon">
                                    <RenderIcons iconName="pen" />
                                </span>
                            </button>
                        </div>
                    )}
                </div>
                <div className="box-content">
                    {!showEdit && !accountInfo.isSSOUser && (
                        <div role="grid" className="data-grid " id="password-grid" aria-hidden="false">
                            <div role="row" className="data-row row">
                                <div role="gridcell" className="col-xs-12 col-sm-4 col-no-gutter">
                                    <div className="data-label" id="label-password-activity">
                                        {tLang("manageAccount.myAccount.manageAccountPassword.label.password")}
                                    </div>
                                </div>
                                <div className="col-xs-12 col-sm-8 col-no-gutter">
                                    <div role="gridcell" className="data-value" aria-labelledby="label-password-activity">
                                        {tLang("manageAccount.myAccount.manageAccountPassword.label.lastChanged")}{" "}
                                        {accountInfo?.lastPasswordChangeDate && getFormattedDate(i18nService.get("Country"), accountInfo.lastPasswordChangeDate)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {showEdit && (
                        <div id="password-form">
                            <form id="password-form-container" onSubmit={handleSubmit(onSubmit)}>
                                <div className="form-row inline row">
                                    <div className="col-xs-12 col-sm-4 col-no-gutter">
                                        <legend className="legend-label"> <label htmlFor="current-password-label"> {tLang("manageAccount.myAccount.manageAccountPassword.label.currentPassword")}</label> </legend>
                                    </div>

                                    <div role="alert" className="form-input col-xs-12 col-sm-4 col-no-gutter">
                                        <input
                                            className="inputbox"
                                            type="password"
                                            id="current-password-label"
                                            name="currentPassword"
                                            ref={register({
                                                required: true,
                                            })}
                                        />
                                        {errors.currentPassword && errors.currentPassword.type === "isPasswordWrong" && (
                                            <span className="form-error">
                                                <p>
                                                    {tLang("manageAccount.myAccount.manageAccountPassword.validation.currentPassword.passwordWrong")} {partnerPhone}
                                                </p>
                                            </span>
                                        )}
                                        {errors.currentPassword && errors.currentPassword.type === "required" && (
                                            <span className="form-error">
                                                <p>{tLang("manageAccount.myAccount.manageAccountPassword.validation.currentPassword.required")}</p>
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="form-row inline row">
                                    <div className="col-xs-12 col-sm-4 col-no-gutter">
                                        <legend className="legend-label"> <label htmlFor="new-password-label"> {tLang("manageAccount.myAccount.manageAccountPassword.label.newPassword")}</label> </legend>
                                    </div>

                                    <div role="alert" className="form-input col-xs-12 col-sm-4 col-no-gutter">
                                        <input
                                            className="inputbox"
                                            type="password"
                                            id="new-password-label"
                                            name="newPassword"
                                            ref={register({ validate: validation })}
                                            onBlur={(e) => setState({ ...state, firstInteractionDone: true })}
                                        />
                                    </div>
                                </div>
                                <div className="form-row inline row">
                                    <div className="col-xs-12 col-sm-4 col-no-gutter"></div>
                                    <div role="alert" className="form-input col-xs-12 col-sm-5 col-no-gutter">
                                        {Object.keys(validationMessage).map((key, value) => {
                                            let validationResultClass;

                                            if (!state.firstInteractionDone && errors && !errors.newPassword) {
                                                validationResultClass = "validation-default";
                                            }

                                            if (state.firstInteractionDone && errors && !errors.newPassword) {
                                                validationResultClass = "validation-true";
                                            }
                                            if (state.firstInteractionDone && errors && errors.newPassword && errors.newPassword.types) {
                                                validationResultClass = errors.newPassword.types[key] ? "validation-false" : "validation-true";
                                            }
                                            if (!state.firstInteractionDone && errors && errors.newPassword && errors.newPassword.types) {
                                                validationResultClass = errors.newPassword.types[key] ? "validation-false" : "validation-true";
                                            }
                                            return (
                                                <span role="alert" key={key} className={validationResultClass}>
                                                    <p>{validationMessage[key]}</p>
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="form-row inline row">
                                    <div className="col-xs-12 col-sm-4 col-no-gutter">
                                        <legend className="legend-label"> <label htmlFor="confirm-password-label"> {tLang("manageAccount.myAccount.manageAccountPassword.label.confirmPassword")}</label> </legend>
                                    </div>

                                    <div role="alert" className="form-input col-xs-12 col-sm-4 col-no-gutter">
                                        <input
                                            type="password"
                                            className={errors.confirmPassword ? "status-error inputbox" : "inputbox"}
                                            ref={register({
                                                required: true,
                                            })}
                                            name="confirmPassword"
                                            id="confirm-password-label"
                                            onBlur={() => {
                                                if (getValues("confirmPassword") !== "" && getValues("confirmPassword") !== watch("newPassword")) {
                                                    setError("confirmPassword", {
                                                        type: "validate",
                                                    });
                                                }
                                            }}
                                        />
                                        {errors.confirmPassword && errors.confirmPassword.type === "validate" && (
                                            <span role="alert" className="form-error">
                                                <p>{tLang("manageAccount.myAccount.manageAccountPassword.validation.confirmPassword.misMatch")}</p>
                                            </span>
                                        )}
                                        {errors.confirmPassword && errors.confirmPassword.type === "required" && (
                                            <span role="alert" className="form-error">
                                                <p>{tLang("manageAccount.myAccount.manageAccountPassword.validation.confirmPassword.required")}</p>
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="margin-vertical-l"></div>
                                <div className="row between-xs">
                                    <div className="col-xs col-no-gutter flex-grow-null"></div>
                                    <div className="col-xs col-no-gutter flex-grow-null">
                                        <ul className="list-inline list-reset">
                                            <li>
                                                <button
                                                    type="button"
                                                    className="btn-default btn-link btn-no-padding btn-no-border btn-min-width"
                                                    onClick={() => hideEditForm()}
                                                >
                                                    <span className="btn-text">{tLang("manageAccount.myAccount.manageAccountPassword.cancelButtonText")}</span>
                                                </button>
                                            </li>
                                            <li className="margin-left-s">
                                                <button type="submit" className="btn-default btn-primary btn-min-width">
                                                    <span className="btn-text">{tLang("manageAccount.myAccount.manageAccountPassword.saveButtonText")}</span>
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
            {showDashboardSecurityQuestionDialog && (
                <DialogComponent hideCloseButton={true} dialogId="dialog-dashboard-security-question" title="" closeDialog={() => {}}>
                    <DashboardSecurityQuestionDialog securityQueData={getQuestionInfo.securityQueData} onClose={() => closeSecurityQuestionDialog()} />
                </DialogComponent>
            )}
        </React.Fragment>
    );
};

export default ManageAccountPassword;
